// ./src/components/features/Reminders/RemindersList/RemindersList.tsx
//
// Event Reminders list — table on desktop (>= md), cards on tablet/mobile. Tap-safe (explicit
// Send/Edit/Delete only), rights-gated per row, selection-ready. A paused reminder is dimmed + badged.
// "Send now" is an inline row mutation (per-button spinner via isSending). Mirrors the Notes list.

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { Reminder } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalRemindersContext } from "@/contexts/RemindersContext";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, LoadMoreButton, CardSkeletonActions, CardSkeletonCheckbox, CardSkeletonLines, countCardActions, type CardSkeletonShape } from "@/components/generic";
import { useEntranceAnimation, entranceListVariants, entranceCardVariants } from "@/hooks/useEntranceAnimation";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

import { kindLabel, kindColor, channelLabel, scheduleSummary, nextSendLabel } from "../reminderDisplay";

// ============================================
// Props
// ============================================

export interface RemindersListProps {
  data?: Reminder[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditReminder?: (reminder: Reminder) => void;
  onDeleteReminder?: (reminder: Reminder) => void;
  onSendNow?: (reminder: Reminder) => void;
  isRemoving?: (id?: string) => boolean;
  isSending?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddReminder?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /** True when a search or filter is applied — an empty list is then a no-match state, not first-run. */
  hasQueryNarrowing?: boolean;
  /** When true, the page is showing starter templates in place of the first-run empty state — so render nothing when empty. */
  emptyStateSuppressed?: boolean;
  /** The event's time zone — send times are shown on the event's clock (ROADMAP #93). ⚠ Required. */
  timeZone: string | null;
}

// ============================================
// Shared bits
// ============================================

const isPaused = (r: Reminder): boolean => r.status === STATUSES.DISABLED;

const ReminderIcon: React.FC<{ reminder: Reminder; size?: number }> = ({ reminder, size = 34 }) => (
  <ThemeIcon variant="light" color={isPaused(reminder) ? "gray" : kindColor(reminder.kind)} radius="xl" size={size}>
    <AppIcon iconName={reminder.channel === "sms" ? "Message" : "Mail"} size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const ReminderBadges: React.FC<{ reminder: Reminder }> = ({ reminder }) => (
  <Group gap={4} wrap="nowrap">
    <Badge size="xs" variant="light" color={kindColor(reminder.kind)}>
      {kindLabel(reminder.kind)}
    </Badge>
    <Badge size="xs" variant="light" color="gray">
      {channelLabel(reminder.channel)}
    </Badge>
  </Group>
);

const StatusBadge: React.FC<{ reminder: Reminder }> = ({ reminder }) => {
  const { t } = useTranslation(["events"]);

  if (isPaused(reminder)) {
    return (
      <Badge size="sm" variant="light" color="gray" leftSection={<AppIcon iconName="PlayerPause" size={10} />}>
        {t("events:reminders.status.paused", { defaultValue: "Paused" })}
      </Badge>
    );
  }

  return (
    <Badge size="sm" variant="light" color="green">
      {t("events:reminders.status.active", { defaultValue: "Active" })}
    </Badge>
  );
};

const RowActions: React.FC<{
  reminder: Reminder;
  onEdit?: (r: Reminder) => void;
  onDelete?: (r: Reminder) => void;
  onSend?: (r: Reminder) => void;
  isDeleting: boolean;
  isSendingRow: boolean;
}> = ({ reminder, onEdit, onDelete, onSend, isDeleting, isSendingRow }) => {
  const { t } = useTranslation(["common", "events"]);

  const canEdit = recordHasRight(reminder, "edit");
  const showEdit = !!onEdit && canEdit;
  const showDelete = !!onDelete && recordHasRight(reminder, "remove");
  const showSend = !!onSend && canEdit;
  const rowBusy = isDeleting || isSendingRow;

  if (!showEdit && !showDelete && !showSend) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showSend && onSend && (
        <Tooltip label={t("events:reminders.actions.sendNow", { defaultValue: "Send now" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="teal"
            size="md"
            onClick={() => onSend(reminder)}
            loading={isSendingRow}
            disabled={rowBusy}
            aria-label={t("events:reminders.actions.sendNow", { defaultValue: "Send now" })}
          >
            <AppIcon iconName="Send" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(reminder)}
            disabled={rowBusy}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(reminder)}
            loading={isDeleting}
            disabled={rowBusy}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  reminder: Reminder;
  onEdit?: (r: Reminder) => void;
  onDelete?: (r: Reminder) => void;
  onSend?: (r: Reminder) => void;
  highlighted: boolean;
  isDeleting: boolean;
  isSendingRow: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
  /** The event's time zone (send times on the event's clock). */
  timeZone: string | null;
}

// ============================================
// Desktop table row
// ============================================

const ReminderTableRow: React.FC<RowProps> = ({
  reminder,
  timeZone,
  onEdit,
  onDelete,
  onSend,
  highlighted,
  isDeleting,
  isSendingRow,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  const paused = isPaused(reminder);
  const name = (reminder.name || "").trim() || kindLabel(reminder.kind);

  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : paused ? 0.7 : 1,
        backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => reminder.id && onToggleSelect?.(reminder.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <ReminderIcon reminder={reminder} />
          <Box style={{ minWidth: 0 }}>
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <Box mt={4}>
              <ReminderBadges reminder={reminder} />
            </Box>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 300 }}>
        <Text size="sm" lineClamp={1}>
          {scheduleSummary(reminder, timeZone)}
        </Text>
        <Text size="xs" c="dimmed" mt={2}>
          {nextSendLabel(reminder, timeZone)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 120 }}>
        <StatusBadge reminder={reminder} />
      </Table.Td>
      <Table.Td style={{ width: 140 }}>
        <RowActions
          reminder={reminder}
          onEdit={onEdit}
          onDelete={onDelete}
          onSend={onSend}
          isDeleting={isDeleting}
          isSendingRow={isSendingRow}
        />
      </Table.Td>
    </Table.Tr>
  );
};

const ReminderTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Box style={{ flex: 1 }}>
          <Skeleton height={14} width="40%" maw={200} mb={6} />
          <Skeleton height={12} width="55%" maw={280} />
        </Box>
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 300 }}>
      <Skeleton height={14} width={180} mb={6} />
      <Skeleton height={10} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Skeleton height={18} width={70} radius="sm" />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const ReminderCardComponent: React.FC<CardProps> = ({
  reminder,
  timeZone,
  onEdit,
  onDelete,
  onSend,
  highlighted,
  isDeleting,
  isSendingRow,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common", "events"]);

  const paused = isPaused(reminder);
  const name = (reminder.name || "").trim() || kindLabel(reminder.kind);
  const toggleSelect = () => reminder.id && onToggleSelect?.(reminder.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    const canEdit = recordHasRight(reminder, "edit");

    if (onSend && canEdit) {
      actions.push({
        key: "send",
        secondary: true,
        icon: "Send",
        label: t("events:reminders.actions.sendNow", { defaultValue: "Send now" }),
        color: "teal",
        onClick: () => onSend(reminder),
        loading: isSendingRow,
        disabled: isDeleting || isSendingRow,
      });
    }

    if (onEdit && canEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(reminder),
        disabled: isDeleting || isSendingRow,
      });
    }

    if (onDelete && recordHasRight(reminder, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(reminder),
        loading: isDeleting,
        disabled: isDeleting || isSendingRow,
      });
    }

    return actions;
  }, [onEdit, onDelete, onSend, reminder, isDeleting, isSendingRow, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : paused ? 0.75 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox
            mt={2}
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <ReminderIcon reminder={reminder} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" justify="space-between">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge reminder={reminder} />
          </Group>
          <Box mt={4}>
            <ReminderBadges reminder={reminder} />
          </Box>
          <Text size="sm" c="dimmed" mt={6} lineClamp={2}>
            {scheduleSummary(reminder, timeZone)}
          </Text>
        </Box>
        {showActions && (
          <RowActions
            reminder={reminder}
            onEdit={onEdit}
            onDelete={onDelete}
            onSend={onSend}
            isDeleting={isDeleting}
            isSendingRow={isSendingRow}
          />
        )}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const ReminderCard = React.memo(ReminderCardComponent);

const ReminderCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={['md', 'xs', 'sm']} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const RemindersListComponent: React.FC<RemindersListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditReminder,
  onDeleteReminder,
  onSendNow,
  isRemoving: isRemovingProp,
  isSending: isSendingProp,
  highlightedId,
  onAddReminder,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
  emptyStateSuppressed = false,
  timeZone,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onSendNow, onEditReminder, onDeleteReminder);

  const remindersContext = useOptionalRemindersContext();

  const reminders = dataProp ?? remindersContext?.reminders ?? [];
  const loading = loadingProp ?? remindersContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? remindersContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? remindersContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? remindersContext?.loadMore;
  const isRemoving = isRemovingProp ?? remindersContext?.isRemoving ?? (() => false);
  const isSending = isSendingProp ?? remindersContext?.isSending ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: reminders.length });

  const handleEdit = useCallback(
    (reminder: Reminder) => {
      closeAllCards();
      onEditReminder?.(reminder);
    },
    [onEditReminder, closeAllCards]
  );

  const handleDelete = useCallback(
    (reminder: Reminder) => {
      closeAllCards();
      onDeleteReminder?.(reminder);
    },
    [onDeleteReminder, closeAllCards]
  );

  const handleSend = useCallback(
    (reminder: Reminder) => {
      closeAllCards();
      onSendNow?.(reminder);
    },
    [onSendNow, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = remindersContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && reminders.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text ta="center" c="dimmed" py="xl">
          {t("events:reminders.noMatch", { defaultValue: "No reminders match your search or filters." })}
        </Text>
      );
    }

    // The page is showing starter templates in place of the first-run empty state.
    if (emptyStateSuppressed) {
      return null;
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("events:reminders.emptyState.title", { defaultValue: "No reminders yet" })}
          description={t("events:reminders.emptyState.description", {
            defaultValue:
              "Schedule reminders to nudge guests as the event approaches, or to chase RSVPs that haven't come in yet.",
          })}
          iconName="Bell"
          actionLabel={t("events:reminders.emptyState.actionLabel", { defaultValue: "Add reminder" })}
          onAction={onAddReminder}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (reminder: Reminder): boolean =>
    isSelectable &&
    ((!!onEditReminder && recordHasRight(reminder, "edit")) || (!!onDeleteReminder && recordHasRight(reminder, "remove")));

  const rowProps = (reminder: Reminder): RowProps => ({
    reminder,
    onEdit: onEditReminder ? handleEdit : undefined,
    onDelete: onDeleteReminder ? handleDelete : undefined,
    onSend: onSendNow ? handleSend : undefined,
    highlighted: reminder.id === highlightedId,
    isDeleting: isRemoving(reminder.id),
    isSendingRow: isSending(reminder.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(reminder),
    selected: selection?.isSelected(reminder.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
    timeZone,
  });

  const selectableVisibleIds = reminders
    .filter((r) => rowIsSelectable(r))
    .map((r) => r.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("events:reminders.list.columns.reminder", { defaultValue: "Reminder" })}</Table.Th>
                <Table.Th style={{ width: 300 }}>
                  {t("events:reminders.list.columns.schedule", { defaultValue: "Schedule" })}
                </Table.Th>
                <Table.Th style={{ width: 120 }}>
                  {t("events:reminders.list.columns.status", { defaultValue: "Status" })}
                </Table.Th>
                <Table.Th style={{ width: 140 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <ReminderTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : reminders.map((reminder) => <ReminderTableRow key={reminder.id} {...rowProps(reminder)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <ReminderCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {reminders.map((reminder) => (
            <motion.div
              key={reminder.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <ReminderCard
                {...rowProps(reminder)}
                forceClose={shouldForceClose(reminder.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(reminder.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(reminder.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const RemindersList = React.memo(RemindersListComponent);
